'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

declare global {
  interface Window {
    __REACT_DEVTOOLS_GLOBAL_HOOK__?: {
      inject: (stuff?: unknown) => void;
    };
  }
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  isMounted: boolean;
}

// Helper to show a refresh prompt when session/token issues occur
const showRefreshPrompt = () => {
  if (typeof window === 'undefined') return;

  const existingPrompt = document.getElementById('joydrop-session-refresh');
  if (existingPrompt) return;

  const prompt = document.createElement('div');
  prompt.id = 'joydrop-session-refresh';
  prompt.style.position = 'fixed';
  prompt.style.zIndex = '2147483647';
  prompt.style.inset = '0';
  prompt.style.display = 'flex';
  prompt.style.alignItems = 'center';
  prompt.style.justifyContent = 'center';
  prompt.style.background = 'rgba(48, 22, 92, 0.4)';

  const card = document.createElement('div');
  card.style.maxWidth = '360px';
  card.style.width = 'calc(100% - 32px)';
  card.style.background = '#ffffff';
  card.style.borderRadius = '16px';
  card.style.padding = '24px';
  card.style.boxShadow = '0 16px 40px rgba(21, 21, 21, 0.12)';
  card.style.textAlign = 'center';
  card.style.fontFamily = '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
  card.style.position = 'relative';

  // Close button
  const closeButton = document.createElement('button');
  closeButton.innerHTML = `
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  `;
  closeButton.style.position = 'absolute';
  closeButton.style.top = '16px';
  closeButton.style.right = '16px';
  closeButton.style.width = '32px';
  closeButton.style.height = '32px';
  closeButton.style.display = 'flex';
  closeButton.style.alignItems = 'center';
  closeButton.style.justifyContent = 'center';
  closeButton.style.border = 'none';
  closeButton.style.background = 'transparent';
  closeButton.style.color = '#6b7280';
  closeButton.style.cursor = 'pointer';
  closeButton.style.borderRadius = '8px';
  closeButton.style.transition = 'all 0.2s';
  closeButton.addEventListener('mouseenter', () => {
    closeButton.style.background = '#f3f4f6';
    closeButton.style.color = '#374151';
  });
  closeButton.addEventListener('mouseleave', () => {
    closeButton.style.background = 'transparent';
    closeButton.style.color = '#6b7280';
  });
  closeButton.addEventListener('click', () => {
    prompt.remove();
  });
  closeButton.setAttribute('aria-label', 'Close');

  const title = document.createElement('h2');
  title.textContent = 'You’ve been away for a bit';
  title.style.margin = '0 0 12px 0';
  title.style.fontSize = '20px';
  title.style.lineHeight = '28px';
  title.style.color = '#1f2937';

  const message = document.createElement('p');
  message.textContent = 'Let’s refresh the page to keep things secure and pick up right where you left off.';
  message.style.margin = '0 0 20px 0';
  message.style.fontSize = '15px';
  message.style.lineHeight = '22px';
  message.style.color = '#4b5563';

  const button = document.createElement('button');
  button.textContent = 'Refresh now';
  button.style.padding = '12px 20px';
  button.style.borderRadius = '9999px';
  button.style.border = 'none';
  button.style.background = '#4c1d95';
  button.style.color = '#ffffff';
  button.style.fontWeight = '600';
  button.style.fontSize = '15px';
  button.style.cursor = 'pointer';
  button.style.boxShadow = '0 10px 20px rgba(76, 29, 149, 0.22)';
  button.addEventListener('click', () => {
    prompt.remove();
    window.location.reload();
  });

  card.appendChild(closeButton);
  card.appendChild(title);
  card.appendChild(message);
  card.appendChild(button);
  prompt.appendChild(card);
  document.body.appendChild(prompt);
};

// Function to suppress React error overlay
const suppressReactErrorOverlay = (): (() => void) => {
  if (typeof window === 'undefined') {
    return () => { };
  }

  const originalConsoleError = console.error;
  const originalDevtoolsInject = window.__REACT_DEVTOOLS_GLOBAL_HOOK__?.inject;
  let cleanupCalled = false;
  let refreshPromptShown = false;

  const handleWindowError = (event: ErrorEvent) => {
    if (
      event.message?.includes('Target container is not a DOM element') ||
      event.message?.includes('hydration') ||
      event.message?.includes('Minified React error #')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
    return false;
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const errorMessage = event.reason?.message || event.reason || '';
    if (
      errorMessage.toString().includes('Network error. Please check your internet connection') ||
      errorMessage.toString().includes('Target container is not a DOM element') ||
      errorMessage.toString().includes('hydration') ||
      errorMessage.toString().includes('Minified React error #')
    ) {
      event.preventDefault();
      event.stopPropagation();
      return true;
    }
    return false;
  };

  const restoreOverrides = () => {
    if (cleanupCalled) return;
    cleanupCalled = true;
    console.error = originalConsoleError;
    window.removeEventListener('error', handleWindowError, true);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    if (process.env.NODE_ENV === 'production' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__ && originalDevtoolsInject) {
      window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = originalDevtoolsInject;
    }
  };

  console.error = (...args) => {
    const invalidTokenError = args.find(arg => {
      if (typeof arg === 'object' && arg !== null) {
        const message = (arg as { message?: string }).message;
        const code = (arg as { code?: unknown }).code;
        return (code === undefined || code === null) &&
          typeof message === 'string' &&
          message.toLowerCase() === 'invalid token';
      }
      if (typeof arg === 'string') {
        return arg.toLowerCase().includes('invalid token');
      }
      return false;
    });

    if (invalidTokenError && !refreshPromptShown) {
      refreshPromptShown = true;
      showRefreshPrompt();
      restoreOverrides();
    }

    const errorMessage = args.join(' ');

    // List of error messages to suppress
    const suppressPatterns = [
      'Network error. Please check your internet connection',
      'Target container is not a DOM element',
      'Hydration failed',
      'Minified React error #',
      'Warning: ',
      'ReactDOM.render is no longer supported',
      'createRoot',
      'hydrateRoot',
      'useLayoutEffect does nothing on the server',
      'Expected server HTML to contain a matching',
      'did not match',
      'Text content did not match',
      'There was an error while hydrating'
    ];

    // Check if error message contains any of the patterns to suppress
    const shouldSuppress = suppressPatterns.some(pattern =>
      errorMessage.includes(pattern)
    );

    if (shouldSuppress) {
      // Suppress the error
      return;
    }

    // Log other errors normally
    originalConsoleError.apply(console, args);
  };

  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

  // Disable React DevTools in production
  if (process.env.NODE_ENV === 'production' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = function () { };
  }

  return restoreOverrides;
};

class ErrorBoundary extends Component<Props, State> {
  private cleanupConsoleOverride?: () => void;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isMounted: false
    };
  }

  componentDidMount() {
    // Suppress React error overlay when component mounts
    this.cleanupConsoleOverride = suppressReactErrorOverlay();

    // Set mounted state to true
    this.setState({ isMounted: true });

    // Add global error handler
    if (typeof window !== 'undefined') {
      window.onerror = (message) => {
        // Suppress specific errors
        if (
          message.toString().includes('Target container is not a DOM element') ||
          message.toString().includes('hydration')
        ) {
          // Prevent default error handling
          return true;
        }
        return false;
      };
    }
  }

  componentWillUnmount() {
    this.cleanupConsoleOverride?.();
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Filter out specific errors we want to suppress
    if (
      error.message.includes('Target container is not a DOM element') ||
      error.message.includes('hydration')
    ) {
      // Don't update state for these errors
      return;
    }

    // For other errors, log them and update state
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });
  }

  private isUnauthorizedError(error: Error): boolean {
    const errorMessage = error.message?.toLowerCase() || '';
    console.log('errorMessage', errorMessage, error.message);
    const errorString = error.toString().toLowerCase();

    return (
      errorMessage?.includes('401') ||
      errorMessage?.includes('invalid token') ||
      errorMessage?.includes('session expired') ||
      errorMessage?.includes('token expired') ||
      errorString?.includes('401') ||
      errorString?.includes('unauthorized') ||
      (error as { status?: number })?.status === 401 ||
      (error as { statusCode?: number })?.statusCode === 401
    );
  }

  render() {
    // If there's an error but it's one we want to suppress, render children anyway
    if (this.state.hasError && this.state.error) {
      const errorMessage = this.state.error.message || '';

      // For specific errors, just render children and ignore the error
      if (
        errorMessage.includes('Target container is not a DOM element') ||
        errorMessage.includes('hydration')
      ) {
        return this.props.children;
      }

      // Check if it's an unauthorized/session expired error
      if (this.isUnauthorizedError(this.state.error)) {
        return (
          <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-50 flex items-center justify-center px-4 py-8">
            <div className="max-w-lg w-full">
              <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 text-center relative overflow-hidden">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-purple-200 rounded-full filter blur-3xl opacity-30 -mr-16 -mt-16"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-200 rounded-full filter blur-3xl opacity-30 -ml-16 -mb-16"></div>

                {/* Content */}
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="mb-6 flex justify-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                  </div>

                  {/* Title */}
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                    Session Expired
                  </h1>

                  {/* Message */}
                  <p className="text-gray-600 text-base sm:text-lg mb-8 leading-relaxed">
                    Your session has expired for security reasons. Please refresh the page to continue or log in again to access your account.
                  </p>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={() => window.location.reload()}
                      className="group relative px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 overflow-hidden"
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh Page
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-700 to-blue-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    </button>

                    <button
                      onClick={() => window.location.href = '/login'}
                      className="px-8 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-purple-400 hover:text-purple-600 transition-all duration-200"
                    >
                      Log In Again
                    </button>
                  </div>

                  {/* Help text */}
                  <p className="mt-6 text-sm text-gray-500">
                    Need help? Contact us at{' '}
                    <a href="mailto:happy@joydrop.me" className="text-purple-600 hover:text-purple-700 font-medium">
                      happy@joydrop.me
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // For other errors, show the fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-blue-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-blue-500 text-6xl mb-4">⚙️</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Just a moment</h1>
            <p className="text-gray-600 mb-4">
              We are setting things up for you. Please wait a moment or try refreshing the page.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;